import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Coins, Zap, AlertCircle, Settings, Bell, BellOff, CreditCard, History, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCY_SYMBOLS = { ZAR: 'R', USD: '$' };

/**
 * Token management UI — shown in the Billing page (admin) and embedded
 * in the Dashboard header as a compact balance pill.
 *
 * Props:
 *   - mode: 'full' | 'compact'   (full = settings + history; compact = pill)
 *   - currency: 'ZAR' | 'USD'
 *   - isAdmin: boolean           (settings + top-up shown only to admins)
 *   - onBalanceChange: fn        (callback for parent to react)
 */
export function TokenManagement({ mode = 'full', currency = 'ZAR', isAdmin = false, onBalanceChange }) {
  const { user } = useAuth();
  const canViewBilling = user?.role === 'admin' || user?.can_view_billing === true;
  const [balance, setBalance] = useState(null);
  const [myBalance, setMyBalance] = useState(null); // per-rep view (Feb 2026 model)
  const [packs, setPacks] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [topupLoading, setTopupLoading] = useState(null); // pack_id while purchasing
  const [invoicePayLoading, setInvoicePayLoading] = useState(null);
  const sym = CURRENCY_SYMBOLS[currency] || 'R';

  const load = useCallback(async () => {
    try {
      const balanceReq = axios.get(`${API}/tokens/balance`);
      const myBalanceReq = axios.get(`${API}/tokens/my-balance`);
      const packsReq = axios.get(`${API}/tokens/packs?currency=${currency}`);
      // Invoices are billing-viewer only — skip the call entirely for others
      const invoicesReq = canViewBilling
        ? axios.get(`${API}/tokens/invoices`).catch(() => ({ data: { invoices: [], total_due_zar: 0 } }))
        : Promise.resolve({ data: { invoices: [], total_due_zar: 0 } });
      const requests = [balanceReq, myBalanceReq, packsReq, invoicesReq];
      if (isAdmin && mode === 'full') {
        requests.push(axios.get(`${API}/tokens/transactions?limit=20`));
      }
      const results = await Promise.all(requests);
      setBalance(results[0].data);
      setMyBalance(results[1].data);
      setPacks(results[2].data);
      setPendingInvoices(results[3].data.invoices || []);
      setInvoiceTotal(results[3].data.total_due_zar || 0);
      if (results[4]) setTransactions(results[4].data.transactions || []);
      if (onBalanceChange) onBalanceChange(results[0].data);
    } catch (e) {
      console.error('Failed to load token data:', e);
    } finally {
      setLoading(false);
    }
  }, [currency, isAdmin, mode, onBalanceChange, canViewBilling]);

  useEffect(() => { load(); }, [load]);

  const updateSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const { data } = await axios.put(`${API}/tokens/settings`, patch);
      setBalance(data);
      if (onBalanceChange) onBalanceChange(data);
      toast.success('Token settings updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTopup = async (packId) => {
    setTopupLoading(packId);
    try {
      const { data } = await axios.post(`${API}/tokens/topup`, {
        pack_id: packId,
        origin_url: window.location.origin,
        currency,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Top-up failed');
      setTopupLoading(null);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    setInvoicePayLoading(invoiceId);
    try {
      const { data } = await axios.post(`${API}/tokens/invoices/${invoiceId}/pay`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start invoice payment');
      setInvoicePayLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading tokens…</span>
      </div>
    );
  }

  if (!balance) return null;

  const isLow = balance.total_balance <= balance.low_balance_threshold;
  const isEmpty = balance.total_balance === 0;

  // ============ COMPACT MODE (Dashboard pill) ============
  // Per-rep model (Feb 2026): show the rep's PERSONAL allowance prominently —
  // that's the number that determines whether their next call gets blocked.
  // The shared overage pool is surfaced as a secondary "+N shared" suffix.
  if (mode === 'compact') {
    const rep = myBalance || {
      rep_monthly_balance: balance.monthly_balance,
      rep_monthly_allowance: balance.monthly_allowance,
      shared_pool_balance: balance.overage_balance,
    };
    const repLeft = rep.rep_monthly_balance ?? 0;
    const repAllow = rep.rep_monthly_allowance ?? 0;
    const shared = rep.shared_pool_balance ?? 0;
    const repEmpty = repLeft === 0;
    const repLow = repAllow > 0 && repLeft <= Math.max(1, Math.floor(repAllow * 0.2)) && repLeft > 0;
    const totallyEmpty = repEmpty && shared === 0;

    const color = totallyEmpty ? 'text-red-700 bg-red-50 border-red-200'
                : repEmpty ? 'text-amber-800 bg-amber-50 border-amber-200'
                : repLow ? 'text-amber-700 bg-amber-50 border-amber-200'
                : 'text-zinc-700 bg-white border-zinc-200';

    const subtitle = repEmpty && shared > 0
      ? `using shared (${shared} left)`
      : repEmpty && shared === 0
        ? 'out — Start Call blocked'
        : `of ${repAllow} this month${shared > 0 ? ` · +${shared} shared` : ''}`;

    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${color}`}
        data-testid="token-balance-pill"
        title={`Your personal allowance: ${repLeft}/${repAllow} · Shared org pool: ${shared}`}
      >
        <Coins className="w-3.5 h-3.5" />
        <span data-testid="token-balance-rep-count">{repLeft} left</span>
        <span className="text-zinc-500 font-normal hidden md:inline" data-testid="token-balance-subtitle">
          {subtitle}
        </span>
      </div>
    );
  }

  // ============ FULL MODE (Billing page tab) ============
  return (
    <div className="space-y-6" data-testid="token-management">
      {/* PENDING INVOICES — admin only, shown if any unpaid */}
      {isAdmin && pendingInvoices.length > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="pending-invoices-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-900">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              Auto-overage invoice{pendingInvoices.length > 1 ? 's' : ''} pending — {sym}{invoiceTotal.toFixed(2)}
            </CardTitle>
            <p className="text-xs text-amber-700 mt-1">
              Auto-purchased tokens from last cycle are bundled into one invoice. Pay now to settle.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingInvoices.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center justify-between p-3 bg-white border border-amber-200 rounded-md"
                  data-testid={`invoice-${inv.id}`}
                >
                  <div>
                    <div className="text-sm font-medium text-zinc-900">
                      {inv.total_tokens} tokens · {sym}{inv.total_amount_zar.toFixed(2)}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      Cycle ending {new Date(inv.cycle_end).toLocaleDateString()} · {inv.charge_count} auto-purchase{inv.charge_count > 1 ? 's' : ''}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handlePayInvoice(inv.id)}
                    disabled={invoicePayLoading !== null}
                    data-testid={`pay-invoice-${inv.id}`}
                  >
                    {invoicePayLoading === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Pay Invoice'}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* BALANCE CARD */}
      <Card className="border-zinc-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Coins className="w-4 h-4 text-amber-500" />
            Meeting Tokens
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-zinc-50 rounded-lg" data-testid="balance-monthly">
              <div className="text-xs text-zinc-500 mb-1">Monthly Allowance</div>
              <div className="text-2xl font-bold text-zinc-900">
                {balance.monthly_balance}
                <span className="text-sm font-normal text-zinc-400"> / {balance.monthly_allowance}</span>
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                Resets {balance.monthly_period_end ? new Date(balance.monthly_period_end).toLocaleDateString() : 'monthly'}
              </div>
            </div>
            <div className="p-3 bg-zinc-50 rounded-lg" data-testid="balance-overage">
              <div className="text-xs text-zinc-500 mb-1">Top-Up Balance</div>
              <div className="text-2xl font-bold text-zinc-900">{balance.overage_balance}</div>
              <div className="text-xs text-zinc-500 mt-1">Persists across cycles</div>
            </div>
            <div className={`p-3 rounded-lg ${isEmpty ? 'bg-red-50' : isLow ? 'bg-amber-50' : 'bg-emerald-50'}`} data-testid="balance-total">
              <div className="text-xs text-zinc-500 mb-1">Total Available</div>
              <div className={`text-2xl font-bold ${isEmpty ? 'text-red-700' : isLow ? 'text-amber-700' : 'text-emerald-700'}`}>
                {balance.total_balance}
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                1 token = 30 min bot time
              </div>
            </div>
          </div>

          {isEmpty && !balance.auto_overage_enabled && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md mb-3">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-800">
                You're out of tokens and auto-overage is disabled — your reps cannot start new bot sessions until you top up or wait for next month's reset.
              </p>
            </div>
          )}
          {isLow && !isEmpty && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md mb-3">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                Tokens are running low. Top up below or wait until {balance.monthly_period_end ? new Date(balance.monthly_period_end).toLocaleDateString() : 'next month'} for the reset.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* TOP-UP PACKS — admin only */}
      {isAdmin && packs && (
        <Card className="border-zinc-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-brand-primary" />
              Top Up Tokens
            </CardTitle>
            <p className="text-xs text-zinc-500 mt-1">
              Top-up tokens persist across monthly resets. The bigger the pack, the lower the per-token price.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="token-packs">
              {Object.entries(packs.packs).map(([pid, p]) => {
                const isBest = pid === 'pack_50';
                return (
                  <div
                    key={pid}
                    className={`relative p-4 rounded-lg border ${isBest ? 'border-blue-300 bg-brand-muted' : 'border-zinc-200 bg-white'}`}
                    data-testid={`pack-${pid}`}
                  >
                    {isBest && (
                      <span className="absolute -top-2 left-3 text-[10px] font-semibold text-brand-primary bg-white border border-blue-300 px-2 py-0.5 rounded-full">
                        Best Value
                      </span>
                    )}
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-2xl font-bold text-zinc-900">{p.tokens}</div>
                      <div className="text-xs text-zinc-500">tokens</div>
                    </div>
                    <div className="text-lg font-semibold text-zinc-800 mb-1">
                      {sym}{p.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-[11px] text-zinc-500 mb-3">
                      {sym}{p.price_per_token}/token
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleTopup(pid)}
                      disabled={topupLoading !== null}
                      className="w-full"
                      data-testid={`buy-${pid}`}
                    >
                      {topupLoading === pid ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        `Buy ${p.tokens} tokens`
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SETTINGS — admin only */}
      {isAdmin && (
        <Card className="border-zinc-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="w-4 h-4 text-zinc-500" />
              Token Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Auto-overage toggle */}
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-zinc-100">
              <div>
                <div className="font-medium text-sm text-zinc-900">Auto-Overage Protection</div>
                <p className="text-xs text-zinc-500 mt-1 max-w-md">
                  Automatically purchase tokens at {sym}{(25).toFixed(2)}/token when reps run out mid-call, so calls aren't disrupted. Off = bot can't start without tokens.
                </p>
              </div>
              <button
                onClick={() => updateSettings({ auto_overage_enabled: !balance.auto_overage_enabled })}
                disabled={savingSettings}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  balance.auto_overage_enabled ? 'bg-emerald-500' : 'bg-zinc-300'
                }`}
                data-testid="toggle-auto-overage"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    balance.auto_overage_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Auto-overage cap */}
            {balance.auto_overage_enabled && (
              <div className="flex items-center justify-between gap-4 pb-4 border-b border-zinc-100">
                <div>
                  <div className="font-medium text-sm text-zinc-900">Monthly Auto-Overage Cap</div>
                  <p className="text-xs text-zinc-500 mt-1">
                    Max tokens to auto-purchase per cycle. Used: <strong>{balance.auto_overage_used_this_cycle}</strong> / {balance.auto_overage_monthly_cap}
                  </p>
                </div>
                <Input
                  type="number"
                  min={0}
                  value={balance.auto_overage_monthly_cap}
                  onChange={(e) => setBalance({ ...balance, auto_overage_monthly_cap: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                  onBlur={() => updateSettings({ auto_overage_monthly_cap: balance.auto_overage_monthly_cap })}
                  className="w-24"
                  data-testid="cap-input"
                />
              </div>
            )}

            {/* Low-balance threshold */}
            <div>
              <div className="flex items-start gap-2 mb-2">
                <Bell className="w-4 h-4 text-zinc-400 mt-0.5" />
                <div>
                  <div className="font-medium text-sm text-zinc-900">Low-Balance Alert Threshold</div>
                  <p className="text-xs text-zinc-500 mt-1">
                    Email admins when total tokens drop to this number.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                {(packs?.low_balance_thresholds || [3, 5, 10]).map((t) => (
                  <button
                    key={t}
                    onClick={() => updateSettings({ low_balance_threshold: t })}
                    disabled={savingSettings}
                    className={`flex-1 sm:flex-none px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                      balance.low_balance_threshold === t
                        ? 'border-brand-primary bg-brand-muted text-brand-primary'
                        : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'
                    }`}
                    data-testid={`threshold-${t}`}
                  >
                    {balance.low_balance_threshold === t && <Check className="inline w-3 h-3 mr-1" />}
                    {t} tokens
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TRANSACTION HISTORY — admin only */}
      {isAdmin && transactions.length > 0 && (
        <Card className="border-zinc-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4 text-zinc-500" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1" data-testid="token-transactions">
              {transactions.slice(0, 15).map((t, i) => {
                const isCredit = t.amount > 0;
                return (
                  <div key={i} className="flex items-center justify-between py-2 px-3 hover:bg-zinc-50 rounded text-sm">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        isCredit ? 'bg-emerald-500' : 'bg-zinc-400'
                      }`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-zinc-700 truncate">{t.note || t.type}</div>
                        <div className="text-[11px] text-zinc-400">
                          {new Date(t.occurred_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className={`text-sm font-mono font-semibold ${isCredit ? 'text-emerald-600' : 'text-zinc-700'}`}>
                      {isCredit ? '+' : ''}{t.amount}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default TokenManagement;
